import React, { useState, useEffect } from 'react';
import { useOrganization, useUser } from '@clerk/clerk-react';
import { FiUsers, FiChevronDown } from 'react-icons/fi';

const OrganizationSelector = ({ onOrganizationChange, selectedOrgId = null }) => {
  const { user } = useUser();
  const { organization, setActive } = useOrganization();
  const [userOrganizations, setUserOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get user's organizations from Clerk
  useEffect(() => {
    const getUserOrganizations = async () => {
      if (user) {
        try {
          // Get user's organization memberships from Clerk
          const memberships = await user.getOrganizationMemberships();
          const orgs = memberships.data.map(membership => ({
            id: membership.organization.id,
            name: membership.organization.name,
            imageUrl: membership.organization.imageUrl,
            role: membership.role,
            membersCount: membership.organization.membersCount
          }));
          setUserOrganizations(orgs);
          
          // Set initial organization if none selected
          if (orgs.length > 0 && !selectedOrgId) {
            const initialOrg = orgs.find(org => org.id === organization?.id) || orgs[0];

            if (onOrganizationChange) {
              onOrganizationChange(initialOrg);
            }
          }
        } catch (error) {
          console.error('Error fetching user organizations:', error);
          setUserOrganizations([]);
        } finally {
          setLoading(false);
        }
      }
    };

    getUserOrganizations();
  }, [user, organization, selectedOrgId, onOrganizationChange]);

  const handleOrganizationChange = async (orgId) => {
    try {
      // Set the active organization in Clerk
      await setActive({ organization: orgId });
      
      // Find the organization object
      const selectedOrg = userOrganizations.find(org => org.id === orgId);
      
      // Notify parent component with the full organization object
      if (onOrganizationChange) {
        onOrganizationChange(selectedOrg);
      }
    } catch (error) {
      console.error('Error switching organization:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2 text-gray-500">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
        <span className="text-sm">Loading organizations...</span>
      </div>
    );
  }

  if (userOrganizations.length === 0) {
    return (
      <div className="flex items-center space-x-2 text-gray-500">
        <FiUsers size={16} />
        <span className="text-sm">No organizations available</span>
      </div>
    );
  }

  if (userOrganizations.length === 1) {
    const org = userOrganizations[0];
    return (
      <div className="flex items-center space-x-2">
        {org.imageUrl && (
          <img 
            src={org.imageUrl} 
            alt={org.name}
            className="w-6 h-6 rounded-full"
          />
        )}
        <div className="flex flex-col">
          <span className="text-sm font-medium text-gray-800">{org.name}</span>
          <span className="text-xs text-gray-500">
            {org.membersCount} member{org.membersCount !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
    );
  }

  // Multiple organizations - show dropdown
  const currentOrg = userOrganizations.find(org => org.id === (selectedOrgId?.id || selectedOrgId || organization?.id)) || userOrganizations[0];

  return (
    <div className="relative">
      <select
        value={currentOrg?.id || ''}
        onChange={(e) => handleOrganizationChange(e.target.value)}
        className="appearance-none bg-white border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        {userOrganizations.map(org => (
          <option key={org.id} value={org.id}>
            {org.name}
          </option>
        ))}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
        <FiChevronDown className="w-4 h-4 text-gray-400" />
      </div>
    </div>
  );
};

export default OrganizationSelector; 